import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import HeroCarouselSlideComponent from './HomepageHeroCarouselSlide.styles';
import { PromoWrap, Slide, StyledAnimatedDiv } from './HomepageHeroCarouselSlide.styles';
import { Image } from '@sitecore-jss/sitecore-jss-react';
import Text from '../shared/JssOverridesText';
import Button from '../shared/Button/Button.jsx';
import { useDeviceResize } from '../../hooks';
import useResizeObserver from '../../hooks/useResizeObserver';
import { isEditorActive, withSitecoreContext, mediaApi } from '@sitecore-jss/sitecore-jss-react';

const HomepageHeroCarouselSlide = ({
  rendering,
  props,
  setCurrentSlide = () => { },
  totalSlides = 1,
  setPause = () => { },
  bind,
  brandHeadlineSvg = {},
  sitecoreContext,
  goToNextSlide = () => { },
  goToPrevSlide = () => { },
  isActive = false,
  onResize = () => { },
}) => {
  const fields = rendering?.fields;
  const params = rendering?.params;
  const experienceEditor = isEditorActive();
  const device = useDeviceResize();
  const slideRef = useRef();
  const { site } = sitecoreContext;
  const dimensions = useResizeObserver(slideRef);

  useEffect(() => {
    if (isActive && dimensions?.height) {
      onResize(dimensions.height);
    }
  }, [isActive, dimensions, onResize]);

  if (experienceEditor || sitecoreContext?.pageEditing) {
    return (
      <HeroCarouselSlideComponent site={site.name} className={site.name}>
        <div className="placeholder-slide">
          {brandHeadlineSvg?.value?.src && !!params?.showBrandHeadline && (
            <div className="tagline">
              <div className="tagline-wrap">
                <h1>That Beautiful Bean Co.</h1>
                <Image field={brandHeadlineSvg} draggable={false} />
              </div>
            </div>
          )}
          <div
            className={
              site.name === 'bushs-beans-foodservices' ? 'fs-image-container' : 'image-container'
            }
          >
            {fields?.backgroundMedia?.value?.src && (
              <img
                src={mediaApi.updateImageUrl(fields?.backgroundMedia?.value?.src, {
                  mw: 2000,
                  mh: 2000,
                })}
                className="background-image-obj"
                draggable={false}
                alt={fields.backgroundMedia.value.alt || ''}
                fetchPriority={isActive ? 'high' : 'auto'}
                loading={isActive ? 'eager' : 'lazy'}
              />
            )}
            {fields?.foregroundImage?.value?.src && (
              <img
                src={mediaApi.updateImageUrl(fields?.foregroundImage?.value?.src, {
                  mw: 1200,
                  mh: 1200,
                })}
                className="front-image"
                draggable={false}
                alt={fields.foregroundImage.value.alt}
                loading={isActive ? 'eager' : 'lazy'}
              />
            )}
            <div
              className={`controls ${
                params?.contentPanelAlignment === 'right' ? 'controls-align-left' : ''
              }`}
            >
              <Button
                type="circle"
                variant={params?.arrowStyle}
                size={device === 'desktop' ? 'large' : 'small'}
                ariaLabel="Next slide"
                title="Next slide"
                onClick={() => {
                  setCurrentSlide((current) => (current === totalSlides - 1 ? 0 : current + 1));
                  setPause(true);
                }}
              />

              <Button
                type="circle"
                variant={params?.arrowStyle}
                size={device === 'desktop' ? 'large' : 'small'}
                ariaLabel="Previous slide"
                title="Previous slide"
                onClick={() => {
                  setCurrentSlide((current) => (current > 0 ? current - 1 : totalSlides - 1));
                  setPause(true);
                }}
              />
            </div>
          </div>
          <PromoWrap
            site={site.name}
            className={`${site.name} ${
              params?.contentPanelAlignment === 'right' ? 'promo-align-right' : ''
            }`}
            variant={params?.contentPanelStyle}
          >
            {fields?.panelTitle?.value && (
              <Text
                field={fields.panelTitle}
                tag={site.name === 'bushs-beans-foodservices' ? 'h1' : 'h2'}
              />
            )}
            {fields?.panelCopy?.value && <Text field={fields.panelCopy} tag="p" />}
            {fields?.panelCta?.value && (
              <Button
                variant="secondary"
                text={fields.panelCta.value?.text}
                href={fields.panelCta.value?.href}
              ></Button>
            )}
          </PromoWrap>
        </div>
      </HeroCarouselSlideComponent>
    );
  }

  return (
    <StyledAnimatedDiv
      {...bind()}
      style={props}
      data-site={site.name}
      className={`slide-wrapper ${site.name}-slide-wrapper`}
      direction={props?.direction}
      role="group"
      aria-roledescription="slide"
      aria-label={`Slide ${(rendering?.slideIndex ?? 0) + 1} of ${totalSlides}`}
      aria-hidden={!isActive}
    >
      <Slide className="slide" ref={slideRef}>
        {site && site.name !== 'bushs-beans-foodservices' && (
          <>
            {brandHeadlineSvg?.value?.src && !!params?.showBrandHeadline && (
              <div className="tagline">
                <div className="tagline-wrap">
                  <h1 className="tagline-h1">
                    That <span>Beautiful</span> Bean Co.
                  </h1>
                  <Image field={brandHeadlineSvg} draggable={false} />
                </div>
              </div>
            )}
          </>
        )}
        <div
          className={
            site.name === 'bushs-beans-foodservices' ? 'fs-image-container' : 'image-container'
          }
          onMouseEnter={() => {
            setPause(true);
          }}
          onMouseLeave={() => {
            setPause(false);
          }}
        >
          {fields?.backgroundMedia?.value?.src && (
            <img
              src={mediaApi.updateImageUrl(fields?.backgroundMedia?.value?.src, {
                mw: 2000,
                mh: 2000,
              })}
              className="background-image-obj"
              draggable={false}
              alt={fields.backgroundMedia.value.alt || ''}
              fetchPriority={isActive ? 'high' : 'auto'}
              loading={isActive ? 'eager' : 'lazy'}
            />
          )}
          {fields?.foregroundImage?.value?.src && (
            <img
              src={mediaApi.updateImageUrl(fields?.foregroundImage?.value?.src, {
                mw: 1200,
                mh: 1200,
              })}
              className="front-image"
              draggable={false}
              alt={fields.foregroundImage.value.alt}
              fetchPriority={isActive ? 'high' : 'auto'}
              loading={isActive ? 'eager' : 'lazy'}
            />
          )}
          {totalSlides > 1 && (
            <div
              className={`controls ${
                params?.contentPanelAlignment === 'right' ? 'controls-align-left' : ''
              }`}
            >
              <Button
                type="circle"
                icon="chevron-left"
                variant={params?.arrowStyle}
                size={device === 'desktop' ? 'large' : 'small'}
                ariaLabel="Previous slide"
                title="Previous slide"
                onClick={() => {
                  goToPrevSlide();
                  setPause(true);
                }}
              />

              <Button
                type="circle"
                icon="chevron-right"
                variant={params?.arrowStyle}
                size={device === 'desktop' ? 'large' : 'small'}
                ariaLabel="Next slide"
                title="Next slide"
                onClick={() => {
                  goToNextSlide();
                  setPause(true);
                }}
              />
            </div>
          )}
        </div>
        {!!(
          fields?.panelTitle?.value ||
          fields?.panelCopy?.value ||
          fields?.panelCta?.value?.href
        ) && (
          <PromoWrap
            site={site.name}
            className={`${params?.contentPanelAlignment === 'right' ? 'promo-align-right' : ''} ${
              site.name === 'bushs-beans-foodservices' ? 'foodservice' : ''
            }`}
            variant={params?.contentPanelStyle}
          >
            {fields?.panelTitle?.value && (
              <Text
                field={fields.panelTitle}
                tag={site.name === 'bushs-beans-foodservices' ? 'h1' : 'h2'}
              />
            )}
            {fields?.panelCopy?.value && <Text field={fields.panelCopy} tag="p" />}
            {fields?.panelCta?.value && (
              <Button
                variant={site.name === 'bushs-beans-foodservices' ? 'brown' : 'secondary'}
                text={fields.panelCta.value?.text}
                href={fields.panelCta.value?.href}
                type="default"
              ></Button>
            )}
          </PromoWrap>
        )}
      </Slide>
    </StyledAnimatedDiv>
  );
};

HomepageHeroCarouselSlide.propTypes = {
  rendering: PropTypes.object,
  props: PropTypes.object,
  setCurrentSlide: PropTypes.func,
  totalSlides: PropTypes.number,
  setPause: PropTypes.func,
  bind: PropTypes.func,
  brandHeadlineSvg: PropTypes.object,
  sitecoreContext: PropTypes.object,
  goToNextSlide: PropTypes.func,
  goToPrevSlide: PropTypes.func,
  isActive: PropTypes.bool,
  onResize: PropTypes.func,
};

export default withSitecoreContext()(HomepageHeroCarouselSlide);